import { Box } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { Controller, FormProvider, useFieldArray, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import andOricon from "../../assets/icons/and-or-icon.svg";
import errorIcon from "../../assets/icons/error-icon.svg";
import BackArrow from "../../assets/icons/left-pointing-arrow-pink.svg";
import { useAppMe, useCountryList } from "../../services/okta";
import {
  useDropDownList,
  useGetSegmentDetail,
  useUpdateSegmentEndDate,
  useSegmentCampaignsList
} from "../../services/segment/index";
import DatasetDropdown from "../../shared/components/dataset-dropdown/DatasetDropdown";
import { useStickyHeader } from "../../shared/hooks/StickyHeader";
import { useSimulationParams } from "../../shared/hooks/useSimulationParams";
import formatValuesSegment from "../../utils/formatValuesSegment";
import { BorderSecondary, CircularProgressCustom, PrimaryButton } from "../../utils/muiCustomTheme";
import DynamicNestedDropdownSegmentSection from "../createSegment/DynamicNestedDropdownSegmentSection";
import InsightsWrap from "../createSegment/InsightsWrap";
import DialogDeleteComponent from "./DialogDeleteComponent";
import DialogEditComponent from "./DialogEditSegment";
import "./SegmentSummary.scss";
import SegmentUsageTable from "./SegmentUsageTable";
import EditHistoryTable from "./EditHistoryTable";
import ErrorPanel from "../../shared/components/error-panel/ErrorPanel";

const safeJsonParse = <T,>(value: unknown, fallback: T): T => {
  if (typeof value !== "string") return fallback;
  try {
    return JSON.parse(value) as T;
  } catch {
    return fallback;
  }
};

const OperatorSwitch = ({ operator }: { operator: string }) => {
  return (
    <div className="dropdown__switch">
      <div className="dropdown__switch__wrap">
        <div className={`dropdown__switch__wrap__and ${operator === "AND" ? "active" : ""}`}>
          And
        </div>
        <div className={`dropdown__switch__wrap__or ${operator === "OR" ? "active" : ""}`}>
          Or
        </div>
      </div>
    </div>
  );
};

const SegmentSummary = () => {
  const { segmentId } = useParams();

  const { data: dataDropdown, isPending: isPendingDataDropdown } = useDropDownList({});
  const { data: countryData } = useCountryList();
  const countryTags = useMemo(() => {
    const tags = (countryData ?? []).flatMap((c: any) => c.tags);
    return Array.from(new Set(tags));
  }, [countryData]);
  const {
    data: dataSegmentDetail,
    isPending: isPendingDataSegmentDetail,
    isFetching: isFetchingDataSegmentDetail,
    error: segmentError,
  } = useGetSegmentDetail({
    query: {
      segmentId,
      tags:countryTags,
    },
    options: {
      enabled: !!segmentId && countryTags.length > 0,
    },
  });

  const { data: dataAppMe, isPending: isPendingAppMe } = useAppMe({});
  const sub = dataAppMe?.data?.user_id;

  const parsedSegmentDetail = useMemo(() => {
    return safeJsonParse<any[]>(dataSegmentDetail?.data ?? "[]", []);
  }, [dataSegmentDetail?.data]);

  const {
    segmentName,
    build,
    country,
    count = 0,
    engaged = 0,
    reachable = 0,
    uniqueEmails = 0,
    brand,
    folder,
    campaign_names,
  } = parsedSegmentDetail?.[0] || {};

  const parsedBuild = useMemo(() => safeJsonParse<any>(build ?? "{}", {}), [build]);
  // Fetch segment campaigns data
  const { data: segmentCampaignsData, isPending: isPendingSegmentCampaigns } = useSegmentCampaignsList({
    query: { segmentId },
    options: { enabled: !!segmentId && !isPendingDataSegmentDetail && !!segmentName },
  });

  const { exclude = {}, include = {} } = parsedBuild || {};

  const tempVal = useMemo(() => {
    return formatValuesSegment({
      country: country ?? "Unknown",
      brand,
      folder,
      user: sub,
      segmentName,
      exclude,
      include,
      dataDropdown: dataDropdown?.data || [],
    });
  }, [brand, country, dataDropdown?.data, exclude, folder, include, segmentName, sub]);

  const formOptions = useMemo(
    () => ({
      defaultValues: tempVal,
      values: tempVal,
      disabled: true,
    }),
    [tempVal]
  );

  const methods = useForm<any>(formOptions);

  const { getValues, control } = methods;

  const { fields } = useFieldArray({
    control,
    name: "sections",
  });

  const { fields: fieldsExclusions } = useFieldArray({
    control,
    name: "exclusions",
  });

  const navigate = useNavigate();
  const { headerRef, sentinelRef, isSticky } = useStickyHeader();
  const { createLink } = useSimulationParams();
  const navigateOnMySegments = useCallback(() => {
    navigate(createLink("/segments"));
  }, [createLink, navigate]);

  const navigateOnSegment = useCallback(() => {
    navigate(createLink(`/create/segment?id=${segmentId}`));
  }, [createLink, navigate, segmentId]);

  const [showEditSegmentDialog, setShowEditSegmentDialog] = useState(false);

  const closeEditSegmentDialog = useCallback(() => {
    setShowEditSegmentDialog(false);
  }, []);
  const [showDeleteSegmentDialog, setShowDeleteSegmentDialog] = useState(false);

  const { mutate: deleteSegment, isPending: isPendingDeleteSegment } = useUpdateSegmentEndDate({
    options: {
      onSuccess: (res: any) => {
        if (res) {
          navigate(createLink("/segments"));
        }
      },
      onError: (err: any) => {
        setShowDeleteSegmentDialog(false);
      },
    },
  });

  const closeDeleteSegmentDialog = useCallback(() => {
    setShowDeleteSegmentDialog(false);
  }, []);

  const handleDeleteSegment = useCallback(() => {
    deleteSegment({ segmentId, segmentName, user: sub });

    setShowDeleteSegmentDialog(false);
  }, [deleteSegment, segmentId, segmentName, sub]);

  // Fetch countries from get_countries API
  //const { data: countryData } = useCountryList();

  // Normalize allowed countries
  const canEdit = useMemo(() => {
    const allowedCountries = (countryData || []).map((c: any) => c.name.trim().toLowerCase());
    const segmentCountry = (country || "").trim().toLowerCase();
    return allowedCountries.includes(segmentCountry);
  }, [country, countryData]);

  if (
    isPendingDataDropdown ||
    isPendingAppMe ||
    isPendingDataSegmentDetail
  ) {
    return (
      <Box
        sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}
      >
        <CircularProgressCustom />
      </Box>
    );
  }

  if (segmentError) {
    const status = (segmentError as any)?.response?.status || (segmentError as any)?.status;
    const message = (segmentError as any)?.response?.data?.message;
    return (
      <ErrorPanel
        code={status}
        message={message}
        primaryLabel="Back to Segments"
        onPrimary={navigateOnMySegments}
      />
    );
  }

  const failedSegmentFetch =
    Array.isArray(parsedSegmentDetail) && parsedSegmentDetail.length === 0;
  if (failedSegmentFetch) {
    return (
      <ErrorPanel
        code={404}
        message="Segment not found or invalid URL."
        primaryLabel="Back to Segments"
        onPrimary={navigateOnMySegments}
      />
    );
  }

  return (
    <FormProvider {...methods}>
      <div className="segment-summary">
        <div ref={sentinelRef}></div>
        <div ref={headerRef} className={`create-segment__wrap ${isSticky ? "is-sticky" : ""}`}>
          <div className="create-segment__wrap__heading-wrap" onClick={navigateOnMySegments}>
            <div className="create-segment__wrap__heading-wrap__icon">
              <img src={BackArrow} alt="Reckitt" />
            </div>
            <div className="create-segment__wrap__heading-wrap__heading">Back to Segments</div>
          </div>
          <div className="create-segment__wrap__btn-wrap">
            <div className="create-segment__wrap__btn-wrap__label">
              {getValues("country") || "Unkown Country"} |{" "}
              {getValues("segmentName") || "Unkown Segment"}
            </div>
            {canEdit && (
              <div className="create-segment__wrap__btn">
                <BorderSecondary
                  type="button"
                  onClick={() => !isPendingDeleteSegment && setShowDeleteSegmentDialog(true)}
                >
                  {isPendingDeleteSegment ? "Deleting..." : "Delete"}
                </BorderSecondary>
                <PrimaryButton variant="contained" onClick={() => setShowEditSegmentDialog(true)}>
                  Edit
                </PrimaryButton>
              </div>
            )}
          </div>
          <div className="create-segment__dataset-wrapper">
            <div className="home__dataset-sec">
              <Controller
                control={control}
                name="country"
                render={({ field, fieldState: { error } }) => {
                  return (
                    <>
                      <div className="view-all-attributes__dataset-wrapper__label">Market:</div>
                      <DatasetDropdown
                        value={field.value || ""}
                        disabled={true}
                        onChange={(e) => field.onChange(e.target.value)}
                      />
                      {error?.message && (
                        <div className="create-segment__input-wrap__segment-err">
                          <img src={errorIcon} alt="Error Icon" />
                          {error?.message}
                        </div>
                      )}
                    </>
                  );
                }}
              />
            </div>
          </div>
          <div className="create-segment__header-divider"></div>
        </div>
        <InsightsWrap
          title="Segment insights:"
          insights={[
            {
              key: "uniqueConsumers",
              label: "Unique Consumers",
              value: count,
              requiresRefresh: false,
            },
            {
              key: "engagedConsumers",
              label: "Engaged Consumers",
              value: engaged,
              requiresRefresh: false,
            },
            {
              key: "reachableConsumers",
              label: "Reachable Consumers",
              value: reachable,
              requiresRefresh: false,
            },
            {
              key: "uniqueEmails",
              label: "Unique Emails",
              value: uniqueEmails,
              requiresRefresh: false,
            },
          ]}
        />

        <div className="create-segment__divider"></div>
        <div className="create-segment__usage-table-wrap">
          <div className="create-segment__insights-wrap__head mb-3">Segment usage:</div>
          <SegmentUsageTable data={segmentCampaignsData?.data || segmentCampaignsData} isLoading={isPendingSegmentCampaigns} />
        </div>
        <div className="create-segment__divider"></div>
        <div className="create-segment__insights-wrap__head mb-3">Segment description:</div>

        <div className="create-segment__dropdown-wrap">
          <div className="create-segment__dropdown-wrap__label">
            Include consumers with ALL <img src={andOricon} alt="and/or" /> of the following…
          </div>
        </div>

        {fields?.map((filter: any, index) => (
          <div key={filter.id} className="create-segment__wrap-dynamic-nested-dropdown disabled">
            {index > 0 && <OperatorSwitch operator={filter.operator} />}

            <DynamicNestedDropdownSegmentSection
              controlName="sections"
              indexSection={index}
              removeDelete={true}
              dataDropdown={dataDropdown}
            />
          </div>
        ))}

        {getValues(`sections.0.conditions`)?.length > 0 && (
          <>
            {fieldsExclusions?.length > 0 && (
              <div className="create-segment__dropdown-wrap">
                <div className="create-segment__dropdown-wrap__label">
                  Exclude ALL consumers <img src={andOricon} alt="and/or" /> of the following…
                </div>
              </div>
            )}

            {fieldsExclusions?.map((filter: any, index) => (
              <div
                key={filter.id}
                className="create-segment__wrap-dynamic-nested-dropdown disabled"
              >
                {index > 0 && <OperatorSwitch operator={filter.operator} />}
                <DynamicNestedDropdownSegmentSection
                  controlName="exclusions"
                  indexSection={index}
                  removeDelete={true}
                  dataDropdown={dataDropdown}
                />
              </div>
            ))}
          </>
        )}
        <div className="segment-summary__edit-history-wrap">
          <div className="create-segment__divider"></div>
          <div className="create-segment__insights-wrap__head mb-3">Edit History:</div>
          <EditHistoryTable data={segmentCampaignsData?.data || segmentCampaignsData} isLoading={isPendingSegmentCampaigns} />
        </div>
        <DialogEditComponent
          open={showEditSegmentDialog}
          onClose={closeEditSegmentDialog}
          onEdit={navigateOnSegment}
          type="segment"
          campaignNames={campaign_names}
        />
        <DialogDeleteComponent
          open={showDeleteSegmentDialog}
          onClose={closeDeleteSegmentDialog}
          onDelete={handleDeleteSegment}
          type="segment"
          campaignNames={campaign_names}
          name={segmentName}
        />
      </div>
    </FormProvider>
  );
};

export default SegmentSummary;
